<script lang="ts">
    import { getContext, hasContext, onDestroy, onMount, tick } from 'svelte'
    import { derived as sderived, writable, type Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import { cloneDeep, isEqual } from 'lodash-es'

    import type { HydriaDocument, IDocument, IRevision, StatusType } from 'hydria/HydriaDocument'
    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { lgText, supportedLanguages } from 'hydria/services/language'
    import { notify } from 'hydria/services/notifications'
    import { session } from 'hydria/services/session'
    import { sanchor, sgoto, spath, sreplace } from 'hydria/svelte_helpers'

    import Attachments from 'hydria/components/Attachments.svelte'
    import Date from 'hydria/components/Date.svelte'

    interface Props {
        docId: string
        revId: string
        Document: HydriaDocument
        formIsValid: boolean
        revFields: string[]
        // Si != null, "common" devient visible
        comFields?: string[]
        canUnpublish?: boolean
        canDelete?: boolean
        listUrl?: string
        docSaved?: any
        revSaved?: any
        hasPreviewing?: boolean
        children?: import('svelte').Snippet
        loaded?: () => void
    }

    let { docId, revId, Document, formIsValid, revFields, comFields = undefined, canUnpublish = true, canDelete = $bindable(undefined), listUrl = undefined, docSaved: docSavedContent = $bindable(null), revSaved: revSavedContent = $bindable(null), hasPreviewing = false, children, loaded }: Props = $props()
    if (canDelete === null) canDelete = canUnpublish

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    let status: StatusType | undefined = $state()
    unsubs.push(
        Document.status.subscribe((v) => {
            if (v == 'loaded') {
                tick().then(() => {
                    loadSaved()
                    status = v
                })
            } else {
                status = v
            }
        }),
    )

    const docSaved = sderived(Document.document, ($doc) => $doc)
    const revisions = sderived(Document.revisions, ($rev) => $rev)

    if (!revFields.includes('id')) revFields.push('id')
    if (comFields && !comFields.includes('id')) comFields.push('id')

    const revSavedFull = sderived(Document.revision, ($rev) => $rev)
    const revSaved = sderived(Document.revision, ($rev) => (!$rev ? $rev : Object.fromEntries(Object.entries($rev).filter(([k, v]) => (comFields != null && $rev.lg === 'common' ? comFields : revFields).includes(k)))) as IRevision)

    let docEdit: Writable<IDocument> = hasContext('docEdit') ? (getContext('docEdit') as Writable<IDocument>) : writable(undefined as unknown as IDocument)

    let revEdit: Writable<IRevision> = hasContext('revEdit') ? (getContext('revEdit') as Writable<IRevision>) : writable(undefined as unknown as IRevision)

    function loadSaved(force: boolean = false) {
        if ($docSaved && (!$docEdit || $docSaved.id !== $docEdit.id || force)) docEdit.set(cloneDeep($docSaved))
        // docEditContent = $docEdit
        if ($revSaved && (!$revEdit || $revEdit.id !== $revSaved.id || force)) revEdit.set(cloneDeep($revSaved))
        // revEditContent = $revEdit

        if ($revEdit.id !== revId && !supportedLanguages.includes(revId) && revId !== 'common') {
            sreplace(`/${Document.docType.replace('_', '-')}/${Document.docId}/${$revEdit.id}`)
        }
        if (loaded) loaded()
    }

    async function save() {
        try {
            await Document.saveRevision($revEdit, false)
        } catch (e) {
            notify({
                title: tgettext('Sauvegarde'),
                text: tgettext('La sauvegarde n’a pas pu être effectuée.'),
                type: 'danger',
            })
        }
    }
    async function publish() {
        try {
            await Document.saveRevision($revEdit, true)
        } catch (e) {
            notify({
                title: tgettext('Publication'),
                text: tgettext('Le document n’a pas pu être publié.'),
                type: 'danger',
            })
        }
    }
    async function unpublish() {
        try {
            await Document.unpublish()
        } catch (e) {
            notify({
                title: tgettext('Publication'),
                text: tgettext('Le document n’a pas pu être dépublié.'),
                type: 'danger',
            })
        }
    }

    let removeConfirm = $state(false)
    function preRemoveDocument() {
        removeConfirm = true
    }

    async function remove() {
        let res = await Document.deleteDocument()
        if (res) {
            sgoto(listUrl ? listUrl : `/${Document.docTypePlural.replace('_', '-')}`)
        }
    }

    let showFiles: boolean = $state(false)

    onDestroy(() => {
        if (removeConfirm && document.documentElement.classList.contains('modal-open')) document.documentElement.classList.remove('modal-open')
    })

    let mounted = false
    onMount(() => {
        mounted = true
    })

    $effect(() => {
        if (mounted && docId && revId) {
            tick().then(() => {
                Document.reset(docId, revId)
            })
        }
    })

    unsubs.push(
        docSaved.subscribe((v) => {
            docSavedContent = v
        }),
    )
    unsubs.push(
        revSaved.subscribe((v) => {
            revSavedContent = v
        }),
    )

    // $: formIsValid = revSchema.isValidSync(revEdit)
    let revIsPristine = $derived(isEqual($revSaved, $revEdit))
    $effect(() => {
        if (removeConfirm && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (!removeConfirm && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<style>
    /* Layout */
    .col-container {
        position: relative;
        display: flex;
        align-items: stretch;
        gap: var(--hy-generic-gap);
        flex-direction: column;
    }
    .col-content,
    .col-save {
        display: flex;
        flex-direction: column;
        gap: var(--hy-generic-gap);
    }
    .col-content {
        flex: 0 0 auto;
        display: flex;
        flex-direction: column;
    }
    .col-save {
        flex: 0 0 auto;
    }
    @media (min-width: 768px) {
        .col-container {
            flex-direction: row;
            align-items: start;
        }
        .col-content {
            flex: 1 1 0;
        }
        .col-edit {
            min-height: 619px;
        }
        .col-save {
            position: sticky;
            top: calc(var(--hy-topbar-height) + var(--hy-generic-gap));
            flex: 0 0 250px;
        }
    }

    @media print {
        .col-save {
            display: none !important;
        }
    }

    /* Styling */
    .card-header {
        background-color: var(--save-card-accent);
        color: var(--bs-light);
    }

    .card-rev li {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
    }
    .card-rev li > :nth-child(n-2) {
        flex: 0 0 14px;
    }
    .card-rev li > :nth-child(3) {
        flex: 1 0 0;
    }
</style>

<!-- <svelte:window bind:scrollY /> -->

{#if status == 'not-found' || status == 'error'}
    <div class="alert alert-danger d-flex align-items-center" role="alert">
        <div class="bi flex-shrink-0 me-2">
            <Icon inline icon="mdi:alert" />
        </div>
        <div>
            {#if status == 'not-found'}
                {$_('Le document demandé n’existe pas.')}
            {:else}
                {$_('Une erreur est survenue. Réessayez dans un moment.')}
            {/if}
        </div>
    </div>
{:else if status == 'loading'}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else}
    <div class="col-container">
        {#if showFiles}
            <div class="col-content">
                <Attachments {Document} />
            </div>
        {:else if $revEdit != null}
            <div class="col-content col-edit">
                {@render children?.()}
            </div>
        {/if}

        <div class="col-save">
            <div class="card">
                <div class="card-header">{$_('Options du document')}</div>
                <div class="card-body d-grid gap-2">
                    <div class="btn-group btn-group d-flex">
                        <button type="button" class="btn btn-outline-secondary" data-tooltip={$_('Revenir à la version sauvegardée')} aria-label={$_('Revenir à la version sauvegardée')} onclick={() => loadSaved(true)} disabled={revIsPristine || showFiles}>
                            <Icon inline icon="mdi:undo" />
                        </button>

                        <button type="button" class="btn btn-outline-secondary" data-tooltip={$_('Sauvegarder en brouillon')} aria-label={$_('Sauvegarder en brouillon')} disabled={revIsPristine || !formIsValid || showFiles} onclick={() => save()}>
                            <Icon inline icon="mdi:content-save-outline" />
                        </button>

                        {#if ($revSavedFull && !$revSavedFull.active) || !revIsPristine}
                            <button type="button" class="btn btn-primary" data-tooltip={$_('Publier')} aria-label={$_('Publier')} onclick={() => publish()} disabled={!formIsValid || showFiles}>
                                <Icon inline icon="mdi:publish" />
                            </button>
                        {:else if $revSavedFull && $revSavedFull.active && revIsPristine}
                            <button type="button" class="btn btn-warning" data-tooltip={$_('Dépublier')} aria-label={$_('Dépublier')} onclick={() => unpublish()} disabled={showFiles || !canUnpublish}>
                                <Icon inline icon="mdi:publish-off" />
                            </button>
                        {/if}

                        <button type="button" class="btn btn-danger tt-left" data-tooltip={$_('Suppression du document')} aria-label={$_('Suppression du document')} disabled={!revIsPristine || showFiles || !canDelete} onclick={() => preRemoveDocument()}>
                            <Icon inline icon="mdi:delete-forever" />
                        </button>
                    </div>

                    {#if hasPreviewing && $revSavedFull && $revSavedFull.lg != 'common'}
                        {#if showFiles || !revIsPristine}
                            <span class="btn btn-outline-secondary disabled" style="font-size: 0.9em">{$_('Prévisualisation sur le site web')}</span>
                        {:else}
                            <a class="btn btn-outline-secondary" style="font-size: 0.9em" target="_blank" href={`/json/cms/1/${Document.docType.replace('_', '-')}/full-preview/${docId}/prepare/${$revSavedFull.lg}?hydria-token=${session.token}`}>{$_('Prévisualisation sur le site web')}</a>
                        {/if}
                    {/if}

                    <button type="button" class="btn btn-outline-secondary" onclick={() => (showFiles = !showFiles)} disabled={status != 'ready'}>
                        {#if showFiles}
                            <Icon inline icon="mdi:pencil-outline" /> {$_('Édition')}
                        {:else}
                            <Icon inline icon="mdi:paperclip" /> {$_('Fichiers')}
                        {/if}
                    </button>
                </div>
            </div>

            <div class="card">
                <div class="card-header">{$_('Langues')}</div>
                <div class="card-body">
                    <div class="d-grid gap-2">
                        {#if comFields != null}
                            <a use:sanchor href={spath(`/${Document.docType.replace('_', '-')}/${Document.docId}/common`)} class="btn btn-outline-secondary" class:active={$revSavedFull && $revSavedFull.lg == 'common'}>{$_('Commun')}</a>
                        {/if}
                        {#each supportedLanguages as _lg}
                            <a use:sanchor href={spath(`/${Document.docType.replace('_', '-')}/${Document.docId}/${_lg}`)} class="btn btn-outline-secondary" class:active={$revSavedFull && $revSavedFull.lg == _lg}>{$lgText[_lg]}</a>
                        {/each}
                    </div>
                </div>
            </div>

            <div class="card card-rev">
                <div class="card-header">{$_('Révisions')}</div>
                <div class="card-body">
                    {#if !$revisions.length}
                        <p class="text-center mb-0">{$_('Aucune révisions')}</p>
                    {:else}
                        <ul class="list-unstyled mb-0">
                            {#each $revisions as altRev}
                                <li>
                                    <span>
                                        {#if altRev.active}
                                            <Icon inline icon="mdi:checkbox-marked-circle-outline" />
                                        {/if}
                                    </span>
                                    <span>
                                        {#if $revSavedFull && altRev.id == $revSavedFull.id}
                                            <Icon inline icon="mdi:pencil-outline" />
                                        {/if}
                                    </span>
                                    <span>
                                        {#if $revSavedFull && altRev.id == $revSavedFull.id}
                                            <span class:not-select={!altRev.active}><Date value={altRev.ctime} format="h-table-s" /></span>
                                        {:else}
                                            <a use:sanchor href={spath(`/${Document.docType.replace('_', '-')}/${Document.docId}/${altRev.id}`)}><Date value={altRev.ctime} format="h-table-s" /></a>
                                        {/if}
                                    </span>
                                </li>
                            {/each}
                        </ul>
                    {/if}
                </div>
            </div>
        </div>
    </div>
{/if}

{#if removeConfirm}
    <div class="modal modal-ds-records" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>{$_('Supprimer ce document')}</div>
                </div>

                <div class="modal-body">
                    {$_('Êtes-vous certain de vouloir supprimer de document ?')}
                </div>

                <div class="modal-footer">
                    <button class="btn btn-danger" type="button" onclick={() => remove()}>
                        {$_('Supprimer')}
                    </button>
                    <button class="btn btn-outline-secondary" type="button" onclick={() => (removeConfirm = false)}>{$_('Annuler')}</button>
                </div>
            </div>
        </div>
        <div class="modal-backdrop" onclick={() => (removeConfirm = false)} aria-hidden="true"></div>
    </div>
{/if}
